import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
    Anchor,
    Body1,
    Box,
    DateOfBirthField,
    Flex,
    FormFieldError,
    Heading1,
    HorizontalDivider,
    Stack,
    TextInput,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import type { UseFormReturnType } from '@myphx-engineering/phx-design-system/hooks';

import { ErrorMap, PageLayout } from '../../../../components';
import { DrxMismatchModal } from '../../../../components/alerts/DrxMismatchModal';
import { AccountLockedOutError } from '../../../../components/alerts/errors/AccountLockedOut.error';
import { KratosPasswordError } from '../../../../components/alerts/errors/KratosPasswordError';
import { ConfirmPasswordField } from '../../../../components/form-fields/ConfirmPasswordField';
import { EmailField } from '../../../../components/form-fields/EmailField';
import {
    type FormWithLillyCheckbox,
    LillyTermsCheckbox,
} from '../../../../components/form-fields/LillyTermsCheckbox';
import { PasswordField } from '../../../../components/form-fields/PasswordField';
import { PhoneNumberField } from '../../../../components/form-fields/PhoneNumberField';
import { SubmitButton } from '../../../../components/form-fields/SubmitButton';
import { useLockoutCheck } from '../../../../hooks/use-lockout-check';
import { routes } from '../../../../routes';
import { useSignUpForm } from '../../../sign-up/use-sign-up-form';

import { PharmacyChangeLink } from './PharmacyChangeLink';
import { PolicyCheckbox } from './PolicyCheckbox';
import { TrustedByLink } from './TrustedByLink';

export const LillySignUp = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const appInsights = useAppInsightsContext();
    const { setBackOverrideState } = useNavBar();

    // Read parameters passed in redirect
    const callbackUrl = searchParams.get('callback') || '/home';
    const userId = searchParams.get('user') ?? undefined;
    const prescriberOrderId =
        searchParams.get('prescriberOrderId') ??
        searchParams.get('drxId') ??
        undefined;
    const location = useLocation();
    const state = location.state;

    const formState = state
        ? {
              email: state.email,
              firstName: state.firstName,
              lastName: state.lastName,
              phoneNumber: state.unformattedPhoneNumber ?? state.phoneNumber,
              dobMonth: state.dateOfBirth
                  ? String(Number.parseInt(state.dateOfBirth.split('-')[1], 10))
                  : undefined,
              dobDay: state.dateOfBirth
                  ? String(Number.parseInt(state.dateOfBirth.split('-')[2], 10))
                  : undefined,
              dobYear: state.dateOfBirth
                  ? state.dateOfBirth.split('-')[0]
                  : undefined,
              password: state.password,
              confirmPassword: state.confirmPassword,
          }
        : undefined;

    const hasFormState =
        formState && Object.values(formState).some((value) => value);

    const {
        form,
        firstNameProps,
        lastNameProps,
        dobMonthProps,
        dobDayProps,
        dobYearProps,
        onDobGroupValidation,
        loading,
        errorMessageType,
        lockoutMinutes: lockoutMinutesFromHook,
        showLockoutError: showLockoutErrorFromHook,
        onFormSubmit,
        showDrxModal,
        pendingContinueData,
        onDrxModalMakeChanges,
        onDrxModalContinue,
    } = useSignUpForm({
        userId,
        callbackUrl,
        prescriberOrderId,
        savedFormValues: hasFormState ? formState : undefined,
        partnerAccountConfiguration: {
            initialValues: {
                policyOptIn: false,
                lillyTermsAccepted: false,
            },
            schema: {
                policyOptIn: z.literal(true),
                lillyTermsAccepted: z.literal(true),
            },
        },
    });

    const ErrorMessage = errorMessageType ? ErrorMap[errorMessageType] : null;
    const kratosError = state?.kratosError;

    const {
        lockoutMinutes: lockoutMinutesFromPageLoad,
        showLockoutError: showLockoutErrorFromPageLoad,
    } = useLockoutCheck({
        lockoutError: state?.lockoutError,
        phoneNumber: state?.phoneNumber,
    });

    const trackForSignin = useTrackEvent(
        appInsights,
        'SIGNUP_SIGNIN_CLICK',
        {}
    );
    const onNavigateToSignIn = () => {
        trackForSignin({ message: 'Sign-in link is clicked' });
        navigate(
            { pathname: routes.signIn, search: window.location.search },
            {
                state: {
                    backToSignUp: true,
                },
            }
        );
    };

    const onNavigateBack = useCallback(() => {
        if (state?.backToSignIn) {
            navigate(-1);
        } else {
            // go to lilly-direct unauth home page
            window.location.replace(import.meta.env.VITE_LILLY_UNAUTH_URL);
        }
    }, [state, navigate]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [state, onNavigateBack, setBackOverrideState]);

    useEffect(() => {
        if (hasFormState) {
            form.validate();
        }
    }, [hasFormState, form]);

    const disableSubmitButton = !form.isValid();

    // Merge lockout state from both sources (page load check and form submit check)
    const displayLockoutError =
        showLockoutErrorFromPageLoad || showLockoutErrorFromHook;
    const displayLockoutMinutes =
        lockoutMinutesFromHook ?? lockoutMinutesFromPageLoad;

    // Scroll to top when error appears
    useEffect(() => {
        if (displayLockoutError || errorMessageType || kratosError) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [
        displayLockoutError,
        errorMessageType,
        kratosError,
        displayLockoutMinutes,
        showLockoutErrorFromHook,
    ]);

    const errorHeader = loading
        ? null
        : (displayLockoutError && (
              <AccountLockedOutError
                  minutesUntilUnlock={displayLockoutMinutes}
              />
          )) ||
          (errorMessageType && ErrorMessage && (
              <ErrorMessage onLinkClick={onNavigateToSignIn} />
          )) ||
          (kratosError && <KratosPasswordError errorMessage={kratosError} />);

    // This page mostly duplicates the form from the sign-up page with some additional fields
    // If a third sign up page is needed, we should consider refactoring
    // the form into a shared component
    return (
        <>
            <DrxMismatchModal
                opened={showDrxModal}
                name={`${pendingContinueData?.firstName ?? ''} ${pendingContinueData?.lastName ?? ''}`.trim()}
                dateOfBirth={pendingContinueData?.dateOfBirth ?? '2000-01-01'}
                phoneNumber={pendingContinueData?.unformattedPhoneNumber ?? ''}
                email={pendingContinueData?.email ?? ''}
                onMakeChanges={onDrxModalMakeChanges}
                onContinue={onDrxModalContinue}
            />
            <PageLayout
                loadingOverlay={loading}
                header={errorHeader}
                heading={
                    <Stack gap="sm">
                        <Heading1 ta="center">
                            {t('signUpPage.lilly.heading')}
                        </Heading1>
                        {prescriberOrderId ? (
                            <Body1>{t('signUpPage.fromOrderDetails')}</Body1>
                        ) : null}
                    </Stack>
                }
                footer={
                    prescriberOrderId && (
                        <PharmacyChangeLink
                            prescriberOrderId={prescriberOrderId}
                        />
                    )
                }
            >
                <form
                    id="create-account-form"
                    onSubmit={form.onSubmit(onFormSubmit)}
                >
                    <Stack gap="lg">
                        <Stack p="lg 0">
                            <Stack gap="md">
                                <Stack gap="calc(var(--mantine-spacing-xs) / 2)">
                                    <Flex gap="sm">
                                        <Box flex={1}>
                                            <TextInput
                                                key={form.key('firstname')}
                                                name="firstname"
                                                label={t(
                                                    'fullNameForm.firstName'
                                                )}
                                                error={!!form.errors.name}
                                                required
                                                {...firstNameProps}
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <TextInput
                                                key={form.key('lastname')}
                                                name="lastname"
                                                label={t(
                                                    'fullNameForm.lastName'
                                                )}
                                                error={!!form.errors.name}
                                                required
                                                {...lastNameProps}
                                            />
                                        </Box>
                                    </Flex>
                                    {form.errors.name &&
                                        typeof form.errors.name ===
                                            'string' && (
                                            <FormFieldError
                                                centered={false}
                                                errText={form.errors.name}
                                            />
                                        )}
                                </Stack>
                                <DateOfBirthField
                                    labels={{
                                        heading: t(
                                            'dateOfBirthForm.dateOfBirth'
                                        ),
                                        month: t('dateOfBirthForm.month'),
                                        day: t('dateOfBirthForm.day'),
                                        year: t('dateOfBirthForm.year'),
                                    }}
                                    monthProps={dobMonthProps}
                                    dayProps={dobDayProps}
                                    yearProps={dobYearProps}
                                    groupError={form.errors.dobDate}
                                    onGroupValidation={onDobGroupValidation}
                                />
                                <PhoneNumberField form={form} page="SIGNUP" />
                                <EmailField form={form} page="SIGNUP" />
                                <Stack gap="md">
                                    <PasswordField form={form} page="SIGNUP" />
                                    <ConfirmPasswordField
                                        form={form}
                                        page="SIGNUP"
                                    />
                                </Stack>
                            </Stack>
                        </Stack>

                        <Stack gap="lg">
                            <TrustedByLink />
                            <HorizontalDivider />
                            <PolicyCheckbox form={form} />
                            <LillyTermsCheckbox
                                form={
                                    form as unknown as UseFormReturnType<FormWithLillyCheckbox>
                                }
                            />
                            <SubmitButton
                                form="create-account-form"
                                disabled={disableSubmitButton}
                            >
                                {t('common.continue')}
                            </SubmitButton>
                            <Body1 ta="center" inline>
                                <Trans
                                    i18nKey="lilly.signUpPage.signIn"
                                    components={{
                                        1: (
                                            <Anchor
                                                bold
                                                textDecoration="none"
                                                onClick={onNavigateToSignIn}
                                            />
                                        ),
                                    }}
                                />
                            </Body1>
                        </Stack>
                    </Stack>
                </form>
            </PageLayout>
        </>
    );
};
